import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useCallback, useState } from "react";

import { queryKeys, useProjectPromotionsQuery } from "@/api/hooks";
import {
  classifyApprovalEvidenceFailure,
  currentPublicationOf,
  handoffOnRecordFor,
  useApprovalEvidenceQuery,
  usePublicationsQuery,
  type ApprovalEvidence,
  type ApprovalEvidenceFailure,
  type ApprovalEvidenceState,
} from "@/api/publication";
import { EM_DASH, formatDateTime } from "@/lib/format";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { StaleDataNotice } from "@/ui/states";

import { PublicationHandoffDialog } from "./PublicationHandoffDialog";

/**
 * Manager-approval progress, and the post-merge handoff it unlocks — rungs 2
 * and 4 of the chronology #437 ratified (#445).
 *
 * Rung 3 is not here and cannot be: a release owner approves in GitHub, through
 * their own identity, and the Editor OBSERVES that verdict rather than
 * collecting it. This surface is the observation reaching a clinician who
 * otherwise had no way to learn it — before #445, "Publication requested" was
 * the last thing the lane could ever say.
 *
 * ## The browser fabricates no approval fact
 *
 * Every sentence below is selected by `ApprovalEvidenceState`, which the service
 * derives from HMAC-verified `pull_request_review` deliveries deduplicated by
 * GitHub's immutable review id. Nothing here recomputes a verdict from
 * `merged`, `invalidatedAt` or a head comparison, and nothing infers one from a
 * publication row: eight states in, eight readings out. `bindable` is likewise
 * consumed, never derived — the service computes it with the SAME predicates
 * `POST .../promotions` enforces, so an action offered on `bindable: true` is
 * an action the handoff would accept, and one withheld on `false` is one it
 * would refuse.
 *
 * ## Clinician vocabulary in the primary reading
 *
 * No repository, pull request, check, version SHA or merge appears in the badge
 * or the sentence. Those coordinates are real and a release owner needs them, so
 * they are kept in the support-and-audit disclosure below rather than deleted —
 * the same rank the Promotion card's technical identity block sits at, for the
 * same #203 reason.
 *
 * ## This component does not poll
 *
 * ProjectJourney owns the Overview's bounded snapshot refresh (#799). This
 * standalone evidence surface retains explicit refresh because approval moves
 * in GitHub rather than in response to the browser. Here freshness is
 * refetch-on-mount under the client's defaults, plus the explicit control below
 * — which invalidates the LISTING as well as the evidence, because a publication
 * that has been replaced is a change to which publication is being asked about.
 */

const STATE_LABEL = {
  pending: "Waiting for approval",
  approved: "Approved",
  rejected: "Changes requested",
  changed_head: "Approval no longer current",
  revoked: "Approval withdrawn",
  superseded: "Replaced",
  retryable: "Did not start",
  unavailable: "Not confirmed",
} satisfies Record<ApprovalEvidenceState, string>;

const STATE_TONE = {
  pending: "accent",
  approved: "success",
  rejected: "warning",
  changed_head: "warning",
  revoked: "warning",
  superseded: "neutral",
  retryable: "warning",
  unavailable: "neutral",
} satisfies Record<
  ApprovalEvidenceState,
  "neutral" | "success" | "accent" | "warning"
>;

/**
 * What each reading says happened, and what to do — in that order, in the
 * clinician's own terms.
 *
 * `approved` is absent because it is the one state whose sentence depends on a
 * second field: an approval that the handoff would accept right now asks for
 * the note and the document, and one that would not says the last step is not
 * open yet. Composing that from `bindable` is not a verdict — the service
 * already decided both halves — so it is done at the call site rather than
 * given a member here that would be wrong half the time.
 */
const STATE_COPY = {
  pending:
    "A release owner has been asked to approve this version. Nothing is needed from you until they do.",
  rejected:
    "A release owner asked for changes before this version can be published. Revise the clinical content, then freeze, preview and accept a new version.",
  changed_head:
    "This version changed after it was approved, so the approval no longer covers it. It has to be approved again before publication can continue.",
  revoked:
    "The approval of this version was withdrawn. It has to be approved again before publication can continue.",
  superseded:
    "A newer publication has replaced this one. It is kept as history and cannot be continued.",
  retryable:
    "The publication did not start, so there is nothing to approve yet. Request publication again — asking again never creates a duplicate.",
  unavailable:
    "Approval progress cannot be confirmed for this version. The editor will not guess at it; check again shortly.",
} satisfies Record<Exclude<ApprovalEvidenceState, "approved">, string>;

const APPROVED_AND_BINDABLE =
  "This version has been approved. Send the release team your note and the signed approval to finish.";
const APPROVED_NOT_BINDABLE =
  "This version has been approved. The last step is not open yet; check again shortly.";
/**
 * Approved, and the last step is behind us rather than ahead of us.
 *
 * The sentence has to stop asking for the note the moment the action stops
 * offering to take it, or the surface says both at once — which is what it did:
 * `bindable` stays true after a handoff is minted, so the instruction outlived
 * the button it was instructing about. The detail of WHEN and BY WHOM lives in
 * the record sentence below; this one only has to stop being an instruction.
 */
const APPROVED_AND_SENT = "This version has been approved.";

const FAILURE_COPY = {
  denied: "You are not authorized to see approval progress for this Project.",
  unavailable:
    "Approval progress is not available in this environment yet.",
  error: "Approval progress could not be read. Try again.",
} satisfies Record<ApprovalEvidenceFailure["kind"], string>;

/**
 * The one reading that can never change again.
 *
 * `superseded` is the service's terminal verdict — "This one is history and is
 * never silently revived" — so offering to re-read it would be offering a
 * control that cannot alter what it reports. Every other state can still move:
 * a withdrawn or rejected approval is one re-review away from `approved`, and a
 * changed head is one away from a fresh verdict, so each keeps the control.
 */
function isRefreshable(state: ApprovalEvidenceState): boolean {
  return state !== "superseded";
}

/**
 * The identity of one reading, for latching a refusal that was about it.
 *
 * A refusal proves THIS tuple stale, not the surface. So the latch is keyed to
 * the publication, the approval and the head the refusal concerned: a re-read
 * that returns a genuinely different tuple releases it by not matching, and a
 * re-read that fails leaves `evidence.data` on the same tuple and keeps it held.
 * That is the whole mechanism — no timer, no counter, and nothing that has to be
 * cleared correctly.
 */
function readingKey(evidence: ApprovalEvidence): string {
  return [
    evidence.publicationId,
    evidence.approvalId ?? "",
    evidence.finalHeadSha ?? "",
  ].join(" ");
}

/**
 * The reading, with a floor under it.
 *
 * The fallback exists for the same reason `PublicationStatusView`'s does:
 * generated types describe a contract, they do not validate a response, and a
 * badge with no sentence beneath it is a dead end rather than a compact status.
 * A member added to `ApprovalEvidenceState` still fails the build at
 * `STATE_COPY`, so this never becomes a way to ship an unwritten reading.
 *
 * `handoffSettled` IS THE SAME AUTHORITY THE ACTION USES, and it is a parameter
 * rather than a second derivation for exactly that reason. `state` and
 * `bindable` alone say the handoff WOULD be accepted, never whether one has
 * already happened — so a sentence deciding on them alone went on instructing a
 * send that the surface had already withdrawn, and a completed handoff read as
 * owed and done at the same time. One fact, one source, both consumers.
 */
function sentenceFor(
  evidence: ApprovalEvidence,
  handoffSettled: boolean,
): string {
  if (evidence.state === "approved") {
    if (handoffSettled) {
      return APPROVED_AND_SENT;
    }
    return evidence.bindable ? APPROVED_AND_BINDABLE : APPROVED_NOT_BINDABLE;
  }
  return (
    STATE_COPY[evidence.state] ??
    "Approval progress cannot be read for this version. Open the support detail below for the current record."
  );
}

/**
 * The release-engineering coordinates, kept secondary.
 *
 * Demoted rather than deleted, exactly like the Promotion card's technical
 * identity block: a release owner reconciling an Editor record against GitHub
 * has nothing else to go on, while a clinician reading the sentence above must
 * not have to parse any of it.
 *
 * Text stays selectable rather than navigating on click. #788 supersedes the
 * old prohibition on exposing the pull-request address: the Project header now
 * offers a deliberate Copy PR link action, and this disclosure retains the
 * same server-provided URL as the manual fallback when the Clipboard API is
 * unavailable. Candidate, head and merge coordinates remain secondary here.
 */
function ApprovalAuditDisclosure({ evidence }: { evidence: ApprovalEvidence }) {
  const rows: Array<{ label: string; value: string }> = [
    { label: "Packs repository", value: evidence.repository },
    {
      label: "Pull request",
      value:
        evidence.prNumber === null || evidence.prNumber === undefined
          ? EM_DASH
          : `#${evidence.prNumber}`,
    },
    { label: "Pull request URL", value: evidence.prUrl ?? EM_DASH },
    { label: "Approved head", value: evidence.approvedHeadSha ?? EM_DASH },
    { label: "Final head", value: evidence.finalHeadSha ?? EM_DASH },
    { label: "Merge commit", value: evidence.mergeCommitSha ?? EM_DASH },
    {
      label: "Approving account",
      value: evidence.reviewer?.githubLogin ?? EM_DASH,
    },
    {
      label: "Verdict recorded",
      value: evidence.observedAt ? formatDateTime(evidence.observedAt) : EM_DASH,
    },
    {
      label: "Last observed",
      value: evidence.lastObservedAt
        ? formatDateTime(evidence.lastObservedAt)
        : EM_DASH,
    },
  ];

  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted-foreground">
        Approval support and audit detail
      </summary>
      <dl className="mt-2 space-y-2 rounded-md border border-border bg-muted/30 p-3">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="font-semibold text-muted-foreground">{row.label}</dt>
            <dd>
              <code className="block select-all break-all text-foreground">
                {row.value}
              </code>
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

export function ApprovalProgress({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const publications = usePublicationsQuery(projectId);
  const publication = publications.data
    ? currentPublicationOf(publications.data)
    : null;
  const evidence = useApprovalEvidenceQuery(
    projectId,
    publication?.publicationId ?? null,
  );
  /**
   * Whether a handoff already exists, read from the service rather than
   * remembered.
   *
   * The completed state cannot live in this component. `bindable` says only that
   * the handoff WOULD be accepted — merged, latest live approval on the final
   * head, no standing rejection — and stays true after one has been minted, so a
   * local "sent" flag left the action offered, and a remount cleared the flag
   * while leaving it offered with no record on screen at all. Worse, the service
   * adopts an existing immutable `(candidate, approval)` handoff and keeps its
   * ORIGINAL note and document, so a second submission returns 201 having
   * discarded exactly the evidence the clinician just entered.
   *
   * So the record is the authority. This is the listing the Promotion card
   * already reads, which costs nothing extra and survives a reload for free.
   */
  const promotions = useProjectPromotionsQuery(projectId);

  const [handoffOpen, setHandoffOpen] = useState(false);
  /**
   * The reading a moved-precondition refusal proved stale — see `readingKey`.
   */
  const [movedReading, setMovedReading] = useState<string | null>(null);
  /**
   * The reading this browser has already submitted a handoff for.
   *
   * Local state, deliberately, and it may only ever WITHHOLD the action — never
   * assert that evidence was recorded. That asymmetry is the whole point: the
   * bug being fixed was local state making a CLAIM the service contradicted, and
   * a latch that only closes a control cannot make one.
   *
   * It is needed because `refresh()` invalidates rather than blanking:
   * `promotions.data` keeps the previous, handoff-free listing for the whole
   * refetch, and `isPending` is false throughout it. Without this the action
   * would be offered again in exactly the window between a successful submission
   * and the record arriving — which is the window in which a second submission
   * gets adopted and discarded.
   */
  const [submittedReading, setSubmittedReading] = useState<string | null>(null);

  /**
   * One control, every read.
   *
   * The evidence key extends the listing key, so invalidating the listing's
   * prefix reaches the evidence hanging off whichever publication it selected.
   * Refreshing only the evidence would ask the same — possibly retired —
   * publication again forever. The promotions listing is invalidated alongside
   * it because a handoff minted in another tab is the same kind of news.
   */
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.projectPublications(projectId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.projectPromotions(projectId),
    });
  }, [projectId, queryClient]);

  if (publications.isPending || (publication !== null && evidence.isPending)) {
    return (
      <p className="text-sm text-muted-foreground">
        Checking approval progress…
      </p>
    );
  }

  if (!publications.data) {
    return (
      <ApprovalUnreadable error={publications.error} onRetry={refresh} />
    );
  }

  // No publication row at all: the compact lifecycle status above is the whole
  // story, and an empty approval reading beside it would be a second status
  // saying nothing.
  if (publication === null) {
    return null;
  }

  if (!evidence.data) {
    return <ApprovalUnreadable error={evidence.error} onRetry={refresh} />;
  }

  const reading = evidence.data;
  const handoffOnRecord = promotions.data
    ? handoffOnRecordFor(promotions.data, reading.candidate)
    : null;
  /**
   * Three independent withdrawals, all fail-closed.
   *
   * `bindable: false` withholds the action whatever the state reads, which is
   * what makes a changed head, a withdrawn approval or a replaced publication
   * remove it without a reload the moment the evidence is re-read.
   *
   * A handoff already on record withholds it because the service would adopt
   * that handoff and discard whatever was entered — offering the form could only
   * produce a false claim. While the listing has not answered yet, the action is
   * withheld too: "no handoff exists" is a fact this surface must have, not one
   * it may assume, and withholding for a moment costs nothing while offering
   * wrongly costs a false evidence claim.
   *
   * A refusal already proved for THIS reading withholds it even if the evidence
   * still says otherwise, because a failed re-read leaves the stale reading in
   * place — see `readingKey`.
   */
  const key = readingKey(reading);
  /**
   * The handoff is behind us: on record, or submitted and being read back.
   *
   * Derived ONCE and consumed by both the action and the sentence, because the
   * two disagreeing is the defect this closes. It deliberately excludes the
   * moved-refusal latch and the not-yet-answered listing: neither means a
   * handoff happened, and both are already reasons the action is withheld — a
   * withheld action and a settled handoff are different facts, and collapsing
   * them would trade this contradiction for a quieter one.
   */
  const handoffSettled = handoffOnRecord !== null || submittedReading === key;
  const offersHandoff =
    reading.state === "approved" &&
    reading.bindable &&
    promotions.data !== undefined &&
    !handoffSettled &&
    movedReading !== key;

  return (
    <div className="space-y-2">
      <StaleDataNotice
        visible={evidence.isError}
        updatedAt={evidence.dataUpdatedAt}
        onRetry={refresh}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={STATE_TONE[reading.state]}>
          {STATE_LABEL[reading.state] ?? reading.state}
        </Badge>
      </div>

      <p className="text-sm text-foreground">
        {sentenceFor(reading, handoffSettled)}
      </p>

      {offersHandoff ? (
        <div className="space-y-2">
          <Button type="button" onClick={() => setHandoffOpen(true)}>
            Send the publication note and approval
          </Button>
          <p className="text-sm text-muted-foreground">
            The release team decides when this version is released; the editor
            never releases it.
          </p>
        </div>
      ) : null}

      {/*
        The stored result, in place of the action. Dated from the RECORD's own
        instant rather than from the moment this browser pressed the button, so a
        reload and a second visitor read the same sentence — and so an adopted
        response can never present itself as a fresh submission.
      */}
      {handoffOnRecord !== null ? (
        <p role="status" className="text-sm text-foreground">
          Your note and the signed approval are with the release team
          {handoffOnRecord.requestedAt
            ? `, sent ${formatDateTime(handoffOnRecord.requestedAt)}`
            : ""}
          . They decide when this version is released.
        </p>
      ) : submittedReading === key ? (
        /*
          Submitted, and the record has not been read back yet.
          Deliberately says what THIS browser did — it sent one — and not what
          the release team's record now holds, because those are different
          claims and only the record can make the second one. When the listing
          answers, the sentence above replaces this one; when it cannot be read,
          this stays honest rather than becoming a false confirmation.
        */
        <p role="status" className="text-sm text-foreground">
          Your note and the signed approval were sent. Confirming them against
          the release team&rsquo;s record&hellip;
        </p>
      ) : null}

      {/*
        Said out loud rather than left as a control that quietly vanished. The
        refusal that set this latch is already on screen inside the dialog, but
        the dialog closes with it, so without this sentence the action would
        simply disappear.
      */}
      {movedReading === key && handoffOnRecord === null ? (
        <p role="status" className="text-sm text-foreground">
          This approval can no longer be used. Check approval progress again for
          the current state.
        </p>
      ) : null}

      {isRefreshable(reading.state) ? (
        <Button type="button" variant="outline" size="sm" onClick={refresh}>
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
          Check again
        </Button>
      ) : null}

      <ApprovalAuditDisclosure evidence={reading} />

      {/*
        Mounted only while the action itself is offered, which is what makes the
        withdrawal total: evidence that stops reporting `approved` + `bindable`
        removes the dialog with the button, so an open form cannot go on
        collecting a note for a handoff the service would now refuse.
      */}
      {offersHandoff && handoffOpen ? (
        <PublicationHandoffDialog
          projectId={projectId}
          evidence={reading}
          onClose={() => setHandoffOpen(false)}
          onCompleted={() => {
            // Latch FIRST, then read the record. The latch only withholds the
            // action; the CLAIM about what was recorded comes from the record
            // alone, which is the half that was wrong before.
            setSubmittedReading(key);
            setHandoffOpen(false);
            refresh();
          }}
          onPreconditionMoved={() => {
            // Latch FIRST, then refresh. The refusal is proof about this exact
            // reading and holds whatever the re-read does — including failing,
            // which leaves the stale `approved` + `bindable` data in place and
            // was the fail-open path.
            setMovedReading(key);
            setHandoffOpen(false);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Approval progress could not be read at all.
 *
 * Separate from a failed REFRESH, which keeps the last reading and says so
 * through `StaleDataNotice`. Here there is nothing to show, so the surface says
 * what it does not know rather than rendering an approval state it never
 * received.
 */
function ApprovalUnreadable({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  const failure = classifyApprovalEvidenceFailure(error);
  return (
    <div className="space-y-2">
      <p role="alert" className="text-sm text-destructive">
        {FAILURE_COPY[failure.kind]}
      </p>
      {/*
        A 501 is permanent until the endpoint is deployed and a denial is not
        the clinician's to clear, so neither gets a control that cannot work —
        ADR 0001's no-dead-controls rule, the same way `QueryErrorState` applies
        it.
      */}
      {failure.kind === "error" ? (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
          Try again
        </Button>
      ) : null}
    </div>
  );
}
